import {
  IFarmInputPurchaseOrder,
  ISupplierUserProfile,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  apiGetPurchaseOrder,
  apiGetPurchaseOrderSupportingDocuments,
  apiSavePurchaseOrderSupportingDocument,
} from 'constants/Api';
import {apiGeneratePurchaseOrderPdf} from 'constants/api/purchaseOrders';
import {FarmDetailCard} from 'screens/Dashboard/Invoices/FarmerDetailCard/FarmDetailCard';
import {InvoiceProducts} from 'screens/Dashboard/Invoices/InvoiceProducts/InvoiceProducts';
import {Route} from 'react-router-dom';
import GetAppIcon from '@material-ui/icons/GetApp';
import {ProductState} from 'appredux/reducers/reducer-products';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {formattedPurchaseOrderId} from 'screens/Dashboard/PurchaseOrders/PurchaseOrderTableSetup';
import {PurchaseOrderDetailCard} from 'screens/Dashboard/PurchaseOrders/PurchaseOrderDetail/PurchaseOrderDetailCard';
import {PurchaseOrderStatusesState} from 'appredux/reducers/reducer-purchase-order-statuses';
// eslint-disable-next-line max-len
import {PurchaseOrderStatusHistoryCard} from 'screens/Dashboard/PurchaseOrders/PurchaseOrderDetail/PurchaseOrderStatusHistoryCard';
import {EPurchaseOrderSupportingDocs, VAT} from 'constants/index';
import {SupportingDocuments} from 'components/SupportingDocuments/SupportingDocuments';
import {reduxUpdatePurchaseOrder} from 'appredux/actions';
import {PurchaseOrderLogisticsCard} from './PurchaseOrderLogisticsCard/PurchaseOrderLogisticsCard';

export const PurchaseOrderDetail = (props: {
  user: ISupplierUserProfile;
  purchaseOrderId: number;
  productState: ProductState;
  purchaseOrderStatusesState: PurchaseOrderStatusesState;
  width: Breakpoint;
}) => {
  const {purchaseOrderId, purchaseOrderStatusesState} = props;

  const [tabIndex, setTabIndex] = useState(0);
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [isLoadingPurchaseOrder, setIsLoadingPurchaseOrder] =
    useState<boolean>(false);
  const [purchaseOrder, setPurchaseOrder] = useState<IFarmInputPurchaseOrder>();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const loadPurchaseOrder = useCallback((purchaseOrderId: number) => {
    setIsLoadingPurchaseOrder(true);
    apiGetPurchaseOrder({id: purchaseOrderId})
      .then((purchaseOrder) => {
        setPurchaseOrder(purchaseOrder);
        reduxUpdatePurchaseOrder(purchaseOrder);
      })
      .catch((error) => {
        alert('Error loading purchase order. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingPurchaseOrder(false));
  }, []);

  useEffect(() => {
    loadPurchaseOrder(purchaseOrderId);
  }, [purchaseOrderId, loadPurchaseOrder]);

  const handleDownload = () => {
    if (!purchaseOrder) return;
    setIsGeneratingPdf(true);
    apiGeneratePurchaseOrderPdf(purchaseOrder.id)
      .then((response) => {
        if (response.success && response.url) {
          const newWindow = window.open(response.url, '_blank');
          if (newWindow) newWindow.opener = null;
        } else {
          alert('Failed to generate document. Please try again.');
        }
      })
      .catch(() => alert('Failed to generate document. Please try again.'))
      .finally(() => setIsGeneratingPdf(false));
  };

  const invoiceExclVat = useMemo(() => {
    if (!purchaseOrder) return undefined;
    if (purchaseOrder.invoice.vatExempt) return purchaseOrder.invoice;
    const lineItemsExclVat = purchaseOrder.orders.map((order) => ({
      ...order,
      price: order.price / (1 + VAT),
    }));
    return {...purchaseOrder.invoice, orders: lineItemsExclVat};
  }, [purchaseOrder]);

  const loadSupportingDocuments = useCallback(() => {
    return apiGetPurchaseOrderSupportingDocuments(purchaseOrderId);
  }, [purchaseOrderId]);

  const updateSupportingDocument = useCallback(
    (document: ISupportingDocumentInput) => {
      return apiSavePurchaseOrderSupportingDocument({
        purchaseOrderId,
        ...document,
      }).then(() => undefined);
    },
    [purchaseOrderId],
  );

  return (
    <div
      // fullScreen
      // open={isOpen}
      // onClose={onClose}
      // TransitionComponent={Transition}
      className="order-detail"
    >
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() =>
                  history.location.key
                    ? history.goBack()
                    : history.replace('/purchase-orders')
                }
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            Purchase Order{' '}
            {purchaseOrder ? formattedPurchaseOrderId(purchaseOrder) : ''}
          </Typography>
          {purchaseOrder && (
            <Tooltip title="Download" aria-label="download">
              <span>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleDownload}
                  disabled={isGeneratingPdf}
                  aria-label="download"
                >
                  {isGeneratingPdf ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GetAppIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>
      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="tabs-container"
        style={{display: showTabs ? '' : 'none'}}
      >
        <Tabs
          value={tabIndex}
          onChange={(event: React.ChangeEvent<{}>, tabIndex: number) =>
            setTabIndex(tabIndex)
          }
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={0} label="Details" />
          <Tab value={1} label="Chat" />
        </Tabs>
      </AppBar>
      <Grid container className="container">
        <Grid
          item
          xs={12}
          md={5}
          className="grid-column"
          style={{display: showTabs && tabIndex === 1 ? 'none' : ''}}
        >
          <Paper className="order-info-container" variant="outlined">
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Order Details</Typography>
              </Box>
              <PurchaseOrderDetailCard
                isLoading={isLoadingPurchaseOrder}
                purchaseOrder={purchaseOrder}
                invoiceExclVat={invoiceExclVat}
                purchaseOrderStatuses={
                  purchaseOrderStatusesState.purchaseOrderStatuses
                }
              />
            </Box>
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Farm Details</Typography>
              </Box>
              <FarmDetailCard
                isLoading={isLoadingPurchaseOrder}
                farmer={purchaseOrder?.invoice.farmer}
              />
            </Box>
            <Box className="order-products-container-2">
              <Box className="header-container">
                <Typography variant="subtitle1">Line Items</Typography>
              </Box>
              <InvoiceProducts
                isLoading={isLoadingPurchaseOrder}
                invoice={invoiceExclVat}
                allInputProducts={[]}
                onOrderUpdate={() => null}
                readonly={true}
              />
            </Box>
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Logistics</Typography>
              </Box>
              <PurchaseOrderLogisticsCard
                isLoading={isLoadingPurchaseOrder}
                purchaseOrder={purchaseOrder}
              />
            </Box>
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Progress</Typography>
              </Box>
              <PurchaseOrderStatusHistoryCard
                isLoading={isLoadingPurchaseOrder}
                purchaseOrder={purchaseOrder}
              />
            </Box>
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Supporting Documents
                </Typography>
              </Box>
              <SupportingDocuments
                readonly={false}
                loadSupportingDocuments={loadSupportingDocuments}
                updateSupportingDocument={updateSupportingDocument}
                removeSupportingDocument={async () => {}}
                appendOnly={true}
                titleOptions={[
                  EPurchaseOrderSupportingDocs.Waybill,
                  EPurchaseOrderSupportingDocs.DeliveryNote,
                  EPurchaseOrderSupportingDocs.Invoice,
                ]}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid
          item
          xs={12}
          md={7}
          className="grid-column"
          style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
        ></Grid>
      </Grid>
    </div>
  );
};
